import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, 
  X, 
  RefreshCw,
  Send,
  Trash2,
  Eye,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { 
  getNotifications, 
  getUnreadNotifications, 
  markNotificationRead, 
  deleteNotification,
  getNotificationPriorities,
  sendNotification
} from '../../api/notifications';
import { useAuth } from '../../context/AuthContext';
import styles from './Notifications.module.scss';
import logger from '../../utils/logger';

const Notifications = () => {
  const { userInfo } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [priority, setPriority] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all'); // all, lab, product, accounts, admin
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showSendModal, setShowSendModal] = useState(false);
  const [priorities, setPriorities] = useState([]);
  const roles = [
    { id: 1, name: 'Admin', value: 'admin' },
    { id: 2, name: 'Lab Team', value: 'lab' },
    { id: 3, name: 'Product Team', value: 'product' },
    { id: 4, name: 'Account Team', value: 'accounts' },
    { id: 0, name: 'All Users', value: 'all' }
  ];
  
  // Determine admin status using the same logic as SideBar
  const isAdmin = ['admin', 'ADMIN', 1].includes(userInfo?.role) || 
                  userInfo?.role?.toLowerCase() === 'admin' || 
                  userInfo?.role_id === 1;
  const [sendForm, setSendForm] = useState({
    message: '',
    category: 'system',
    priority: 'medium',
    role_id: 1 // Default to admin
  });
  
  const itemsPerPage = 10;

  // Load notifications function - moved before useEffect
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (filter === 'unread') {
        response = await getUnreadNotifications();
      } else {
        response = await getNotifications({
          skip: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
          priority: priority !== 'all' ? priority : undefined,
          is_read: filter === 'read' ? true : filter === 'unread' ? false : undefined,
          team: teamFilter !== 'all' ? teamFilter : undefined
        });
      }
      setNotifications(response);
      setTotalPages(Math.ceil(response.length / itemsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, priority, teamFilter, currentPage, setLoading, setError, setNotifications, setTotalPages]);

  useEffect(() => {
    loadNotifications();
    loadPriorities();
  }, [filter, priority, teamFilter, currentPage, loadNotifications]);

  const loadPriorities = async () => {
    try {
      const prioritiesRes = await getNotificationPriorities();
      setPriorities(prioritiesRes);
    } catch (err) {
      logger.error('Error loading priorities:', err);
    }
  };


  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!sendForm.message.trim()) return;

    try {
      setLoading(true);
      await sendNotification(sendForm);
      setShowSendModal(false);
      setSendForm({
        message: '',
        category: 'system',
        priority: 'medium',
        role_id: 1
      });
      await loadNotifications();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const getRoleName = (roleId) => {
    if (roleId === 0) return 'All Users';
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : 'Unknown Role';
  };


  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Bell className={styles.titleIcon} />
          <h1>Internal Communications</h1>
          <p>Send and receive notifications within your organization</p>
        </div>
        
        <button
          className={styles.sendButton}
          onClick={() => setShowSendModal(true)}
        >
          <Send size={16} />
          Send Notification
        </button>
      </div>

      {error && (
        <div className={styles.error}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label>Status:</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread Only</option>
            <option value="read">Read Only</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label>Priority:</label>
          <select 
            value={priority} 
            onChange={(e) => setPriority(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Priorities</option>
            {priorities.map(pri => (
              <option key={pri.value} value={pri.value}>
                {pri.label}
              </option>
            ))}
          </select>
        </div>

        {isAdmin && (
          <div className={styles.filterGroup}>
            <label>Team:</label>
            <select 
              value={teamFilter} 
              onChange={(e) => setTeamFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All Teams</option>
              <option value="admin">Admin</option>
              <option value="lab">Lab Team</option>
              <option value="product">Product Team</option>
              <option value="accounts">Account Team</option>
            </select>
          </div>
        )}

        <button
          className={styles.refreshButton}
          onClick={loadNotifications}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? styles.spinning : ''} />
          Refresh
        </button>
      </div>

      <div className={styles.notificationsList}>
        {loading ? (
          <div className={styles.loading}>
            <RefreshCw className={styles.spinning} />
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.empty}>
            <Bell size={48} />
            <h3>No notifications found</h3>
            <p>No notifications match your current filters.</p>
          </div>
        ) : (
          notifications.map(notification => (
            <div 
              key={notification.id} 
              className={`${styles.notificationCard} ${!notification.is_read ? styles.unread : ''}`}
            >
              <div className={styles.notificationHeader}>
                <div className={styles.notificationMeta}>
                  <div className={styles.notificationInfo}>
                    <span className={styles.priority}>
                      {notification.priority.toUpperCase()}
                    </span>
                    <span className={styles.role}>
                      To: {getRoleName(notification.role_id)}
                    </span>
                  </div>
                </div>
                <div className={styles.notificationActions}>
                  {!notification.is_read && (
                    <button
                      className={styles.markReadButton}
                      onClick={() => handleMarkAsRead(notification.id)}
                      title="Mark as read"
                    >
                      <Eye size={16} />
                    </button>
                  )}
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDelete(notification.id)}
                    title="Delete notification"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className={styles.notificationContent}>
                <p className={styles.message}>{notification.message}</p>
                <div className={styles.timestamp}>
                  <Clock size={14} />
                  {formatTimestamp(notification.timestamp)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={styles.paginationButton}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={styles.paginationButton}
          >
            Next
          </button>
        </div>
      )}

      {/* Send Notification Modal */}
      {showSendModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Send Internal Notification</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowSendModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className={styles.modalInfo}>
              <p><strong>Recipients:</strong> Notifications sent to "All Users" will be visible to everyone. Role-specific notifications will only be visible to users with that role and admins.</p>
            </div>
            
            <form onSubmit={handleSendNotification} className={styles.sendForm}>
              <div className={styles.formGroup}>
                <label>Recipients:</label>
                <select
                  value={sendForm.role_id}
                  onChange={(e) => setSendForm(prev => ({ 
                    ...prev, 
                    role_id: parseInt(e.target.value) 
                  }))}
                  className={styles.formSelect}
                  required
                >
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Category:</label>
                <select
                  value={sendForm.category}
                  onChange={(e) => setSendForm(prev => ({ 
                    ...prev, 
                    category: e.target.value 
                  }))}
                  className={styles.formSelect}
                  required
                >
                  <option value="alert">Alert</option>
                  <option value="system">System</option>
                  <option value="purchase">Purchase</option>
                  <option value="formulation">Formulation</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Priority:</label>
                <select
                  value={sendForm.priority}
                  onChange={(e) => setSendForm(prev => ({ 
                    ...prev, 
                    priority: e.target.value 
                  }))}
                  className={styles.formSelect}
                  required
                >
                  {priorities.map(pri => (
                    <option key={pri.value} value={pri.value}>
                      {pri.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Message:</label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) => setSendForm(prev => ({ 
                    ...prev, 
                    message: e.target.value 
                  }))}
                  className={styles.messageInput}
                  placeholder="Enter your notification message..."
                  rows={4}
                  required
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setShowSendModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.sendSubmitButton}
                  disabled={loading || !sendForm.message.trim()}
                >
                  <Send size={16} />
                  {loading ? 'Sending...' : 'Send Notification'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;